import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LifeBuoy, MessageSquare, Paperclip, Plus } from 'lucide-react';
import { useGetSupportTickets } from '@/api/control/control-plane/control-plane';
import type { SupportTicketCountsDto, SupportTicketSummaryDto } from '@/api/control/missionImpactAPI.schemas';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { SupportStatusBadge } from '@/components/support/SupportStatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  RESOLUTION_CATEGORY_LABELS,
  SUPPORT_TAB_STATUS_GROUPS,
  SUPPORT_TICKET_TABS,
  fillSupportTemplate,
  formatSupportDate,
  getDateLocale,
  getSupportPersonLabel,
  getSupportTicketState,
  type SupportTicketTab,
} from '@/lib/support';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;

interface SupportTicketsTableProps {
  orgId: string;
  tab: SupportTicketTab;
  onTabChange: (tab: SupportTicketTab) => void;
  counts: SupportTicketCountsDto | undefined;
}

/**
 * Returns the count shown on a tab, or undefined while counts are loading.
 */
function getTabCount(tab: SupportTicketTab, counts: SupportTicketCountsDto | undefined): number | undefined {
  if (!counts) return undefined;
  return tab === 'open' ? counts.open : tab === 'needsReply' ? counts.needsReply : counts.closed;
}

/**
 * The organization's support tickets, filtered by the Open / Needs your reply / Closed tabs, with
 * pagination. Rows waiting on the customer are marked so they stand out.
 */
export function SupportTicketsTable({ orgId, tab, onTabChange, counts }: SupportTicketsTableProps) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetSupportTickets(orgId, {
    statusGroup: SUPPORT_TAB_STATUS_GROUPS[tab],
    page,
    pageSize: PAGE_SIZE,
  });

  const result = data?.data;
  const tickets: SupportTicketSummaryDto[] = result?.items ?? [];
  const totalPages = Math.max(result?.totalPages ?? 1, 1);
  const newTicketUrl = `/dashboard/orgs/${orgId}/support/tickets/new`;
  const hasNoTicketsAtAll = counts?.total === 0;

  /**
   * Switches tab and returns to the first page.
   */
  const handleTabChange = (next: SupportTicketTab) => {
    setPage(1);
    onTabChange(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.support.tickets.title}</CardTitle>
        <CardDescription>{t.support.tickets.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-0">
        <div className="px-6">
          <div role="tablist" aria-label={t.support.tickets.tabsLabel} className="inline-flex gap-0.5 rounded-lg bg-muted p-[3px]">
            {SUPPORT_TICKET_TABS.map((key) => {
              const selected = key === tab;
              const count = getTabCount(key, counts);
              const needsAttention = key === 'needsReply' && (count ?? 0) > 0;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    selected && 'bg-background text-foreground shadow-sm'
                  )}
                  onClick={() => { handleTabChange(key); }}
                >
                  {t.support.tickets.tabs[key]}
                  {count !== undefined && (
                    <span
                      className={cn(
                        'text-xs',
                        needsAttention &&
                          'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 font-semibold background-warning-strong text-warning-strong'
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {error ? (
          <div className="px-6">
            <QueryErrorState error={error} message={t.support.tickets.loadFailed} onRetry={() => { void refetch(); }} />
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center text-muted-foreground">{t.support.tickets.loading}</div>
        ) : tickets.length === 0 ? (
          hasNoTicketsAtAll ? (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <LifeBuoy className="h-8 w-8 text-muted-foreground" aria-hidden />
              <div className="font-medium">{t.support.tickets.emptyTitle}</div>
              <p className="max-w-xl text-sm text-muted-foreground">{t.support.tickets.emptyBody}</p>
              <Button variant="outline" onClick={() => { void navigate(newTicketUrl); }}>
                <Plus className="mr-2 h-4 w-4" />
                {t.support.tickets.newTicket}
              </Button>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">{t.support.tickets.emptyTab[tab]}</div>
          )
        ) : (
          <>
            <Table className="[&_tr>*:first-child]:pl-6 [&_tr>*:last-child]:pr-6">
              <TableHeader>
                <TableRow>
                  <TableHead>{t.support.tickets.columns.ticket}</TableHead>
                  <TableHead className="w-36">{t.support.tickets.columns.type}</TableHead>
                  <TableHead className="w-36">{t.support.tickets.columns.category}</TableHead>
                  <TableHead className="w-40">{t.support.tickets.columns.status}</TableHead>
                  <TableHead className="w-48">{t.support.tickets.columns.lastActivity}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => {
                  const waiting = getSupportTicketState(ticket) === 'WaitingOnYou';
                  const ticketUrl = `/dashboard/orgs/${orgId}/support/tickets/${ticket.id ?? ''}`;
                  return (
                    <TableRow
                      key={ticket.id}
                      className={cn('cursor-pointer', waiting && '[&>td:first-child]:shadow-[inset_3px_0_0_var(--warning-strong)]')}
                      onClick={() => { if (ticket.id) void navigate(ticketUrl); }}
                    >
                      <TableCell className="max-w-md align-top">
                        <Link
                          to={ticketUrl}
                          className={cn('block truncate hover:underline', waiting ? 'font-semibold' : 'font-medium')}
                          onClick={(e) => { e.stopPropagation(); }}
                        >
                          {ticket.title}
                        </Link>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span>
                            {fillSupportTemplate(t.support.tickets.openedBy, {
                              name: getSupportPersonLabel(ticket.submitter, t.support.person),
                            })}
                          </span>
                          <span aria-hidden>·</span>
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" aria-hidden />
                            <span className="sr-only">
                              {fillSupportTemplate(t.support.tickets.commentCount, { count: ticket.commentCount ?? 0 })}
                            </span>
                            <span aria-hidden>{ticket.commentCount ?? 0}</span>
                          </span>
                          {(ticket.attachmentCount ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Paperclip className="h-3 w-3" aria-hidden />
                              <span className="sr-only">
                                {fillSupportTemplate(t.support.tickets.attachmentCount, { count: ticket.attachmentCount ?? 0 })}
                              </span>
                              <span aria-hidden>{ticket.attachmentCount}</span>
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-sm">
                        {ticket.category ? t.support.tickets.types[ticket.category] : null}
                      </TableCell>
                      <TableCell className="align-top text-sm">
                        {ticket.resolutionCategory ? (
                          RESOLUTION_CATEGORY_LABELS[ticket.resolutionCategory]
                        ) : (
                          <span className="text-muted-foreground">{t.support.tickets.noCategory}</span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <SupportStatusBadge ticket={ticket} />
                      </TableCell>
                      <TableCell className="align-top">
                        {ticket.lastActivity ? (
                          <>
                            <div className="text-sm">{getSupportPersonLabel(ticket.lastActivity.author, t.support.person)}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatSupportDate(ticket.lastActivity.at, locale)}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-muted-foreground">{t.support.tickets.noActivity}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-6 pt-4">
                <span className="text-sm text-muted-foreground">
                  {fillSupportTemplate(t.support.tickets.pageOf, { page, totalPages })}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); }}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {t.support.tickets.previous}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => { setPage((p) => p + 1); }}
                  >
                    {t.support.tickets.next}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
